import { getAuthApiErrorCode } from '@/features/auth/utils/resolveRegisterApiError.js'
import { resolveHttpErrorViewState } from '@/features/api/services/utils/resolveHttpErrorViewState.js'
import { addBreadcrumb, captureException, captureMessage } from '@/features/ui/utils/sentryUtils.js'

export const AUTH_FLOW = {
  REGISTER: 'register',
  LOGIN: 'login',
  SOCIAL_LOGIN: 'social-login',
  SESSION: 'session',
}

const EXPECTED_AUTH_ERROR_CODES = new Set([
  'Invalid_Username_Or_Password',
  'User_Is_Not_Exist',
  'Account_Is_Not_Exist',
  'Email_Already_Exists',
  'User_Has_Been_Already_Registered',
  'User_Has_Been_Already_Registered_Through_Google',
])

const EXPECTED_HTTP_STATUSES = new Set([400, 401, 403, 409, 422, 429])

const EXPECTED_AUTH_REASONS = new Set([
  'redirect-to-register',
  'redirect-to-login',
  'register-failure-response',
  'otp-invalid',
  'validation',
  'invitation-auto-join-failed',
  'invitation-already-member',
  'invitation-resend-failed',
])

function getHttpStatus(error) {
  return error?.response?.status ?? null
}

function resolveHttpStatusI18nKey(error) {
  if (!error || typeof error !== 'object') {
    return null
  }

  const { messageKey, status } = resolveHttpErrorViewState(error)

  if (typeof status !== 'number' || !messageKey) {
    return null
  }

  return messageKey
}

export function resolveLoginApiErrorI18nKey(error) {
  const code = getAuthApiErrorCode(error)

  if (code) {
    return code
  }

  return resolveHttpStatusI18nKey(error) || 'httpErrors.unexpected.message'
}

function isExpectedAuthError({ errorCode, httpStatus, reason }) {
  if (reason && EXPECTED_AUTH_REASONS.has(reason)) {
    return true
  }

  if (errorCode && EXPECTED_AUTH_ERROR_CODES.has(errorCode)) {
    return true
  }

  if (httpStatus && EXPECTED_HTTP_STATUSES.has(httpStatus)) {
    return true
  }

  return false
}

function buildAuthFlowExtra(context) {
  const { flow, step, error, errorCode, reason, i18nKey, tenantId } = context

  return {
    flow,
    step,
    reason,
    errorCode,
    i18nKey,
    tenantId,
    httpStatus: getHttpStatus(error),
    message: error?.message,
  }
}

export function reportAuthFlowError(context) {
  const { flow, step, error, errorCode: explicitCode, reason } = context
  const errorCode = explicitCode ?? (error ? getAuthApiErrorCode(error) : null)
  const httpStatus = getHttpStatus(error)
  const extra = buildAuthFlowExtra({ ...context, errorCode })
  const tags = { feature: 'auth', flow, ...(step ? { step } : {}) }
  const message = `[Auth] ${flow}${step ? `:${step}` : ''} failed${reason ? ` (${reason})` : ''}`

  if (isExpectedAuthError({ errorCode, httpStatus, reason })) {
    captureMessage(message, { level: 'warning', extra, tags })
    return
  }

  if (error instanceof Error) {
    captureException(error, { extra, tags })
    return
  }

  captureMessage(message, { level: 'error', extra, tags })
}

export function addAuthFlowBreadcrumb({ flow, step, message, data = {} }) {
  addBreadcrumb({
    category: 'auth',
    message: message || `[Auth] ${flow}${step ? `:${step}` : ''}`,
    level: 'info',
    data: { flow, step, ...data },
  })
}
